import {
  CRAStatus,
  InvoiceStatus,
  CRADashboardItemGQL,
  ActivityDayDetails,
  ActivityDayType,
  Maybe,
} from '@bloomays-lib/types.shared';
import { styled } from '@mui/material/styles';
import { LoaderSkeleton, ActionButton, FileViewer, Modal, IconButton, HrefLink } from '@bloomays-lib/ui.shared';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RawDataGrid from '../molecules/DataGrid';
import {
  GridCellParams,
  GridColDef,
  GridColumnGroupingModel,
  GridRenderCellParams,
  gridClasses,
  GridValueGetter,
  GridRowClassNameParams,
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns/fp/format';
import CraRelaunchAction from '../molecules/CraRelaunchAction';
import { useEffect, useMemo, useState } from 'react';
import ChronologicalHistory from './ChronologicalHistory';
import DetailsSectionCard from '../molecules/DetailsSectionCard';
import { add } from 'date-fns/fp/add';
import { lastDayOfMonth } from 'date-fns/fp/lastDayOfMonth';
import pipe from 'lodash/fp/pipe';
import values from 'lodash/fp/values';
import Step from '../molecules/Step';
import ActionInvoice from '../molecules/ActionInvoice';
import ActionForceCRA from '../molecules/ActionForceCRA';
import Stack from '@mui/material/Stack';
import { convertActivityToEachDaysArray, isZeroCRAActivity } from '@bloomays-lib/utils.shared';
import { FormCRA } from '../molecules/FormCRA';
import SummaryDaysWorked from './SummaryDaysWorked';
import { calculPerDay } from '../../helpers/CRA';
import { useAuthStore } from '../../stores';
import { getInvoiceStatusOptions, renderInvoiceStatusCell } from '../../helpers/invoiceDatagrid';
import { notify } from '../../helpers/toastify';
import ActionZeroCRA from '../molecules/ActionZeroCRA';
import { ILogger } from '@bloomays-lib/types.shared';
type ActivityDashboardProps = {
  uniqueActivityDashboardId: string;
  missionMode: boolean;
  loading: boolean;
  rows: CRADashboardItemGQL[];
  currentMonth: Date;
  createActivityCustomerInvoice: (v: { variables: { activityRecordID: string; force: boolean } }) => void;
  createActivitySupplierInvoice: (v: {
    variables: { activityRecordID: string; force: boolean; byPassComplianceCheck: boolean };
  }) => void;
  sendBloomerRelaunch: (v: { variables: { missionId?: string; month: string } }) => void;
  sendClientRelaunch: (v: { variables: { signingDocumentId?: string | null } }) => void;
  forceCRA: (v: { variables: { activityRecordID: string; force: boolean } }) => void;
  declareZeroActivityForMission: (v: { variables: { missionId: string; month: Date } }) => void | Promise<void>;
  logger?: ILogger;
};

const statusCRA: CRAStatus[] = [
  'Non crée',
  'Signature non créee',
  'Attente signature Bloomer',
  'Attente signature Client',
  'Validé client',
  'Forcé Bloomays',
  'Non travaillé',
];

const canStatusSupportZeroCRA = (status: CRAStatus) => {
  return status === 'Non crée' || status === 'Attente signature Bloomer' || status === 'Signature non créee';
};

const renderCRAStatusCell =
  (clickHandler: (cRADashboardItemGQL: CRADashboardItemGQL) => () => void) =>
  (params: GridRenderCellParams<CRADashboardItemGQL>) => {
    // rendering for group header rows
    if (params.rowNode.type === 'group') {
      if (params.field !== '__row_group_by_columns_group__') return '';
      const groupingKey = params.rowNode.groupingKey as CRAStatus;
      if (!groupingKey) return;
      return (
        <Stack direction="row" alignItems="center">
          <Step step={(statusCRA.findIndex((s) => s === groupingKey) + 1).toString()}></Step>&nbsp;{groupingKey}
        </Stack>
      );
    }

    // rendering for normal rows
    const status = params.row.status;
    switch (status) {
      case 'Non crée':
      case 'Attente signature Bloomer':
      case 'Attente signature Client':
      case 'Signature non créee':
        return (
          <Clickable direction="row" alignItems="center" onClick={clickHandler(params.row)}>
            <Step step={(statusCRA.findIndex((s) => s === status) + 1).toString()}></Step>&nbsp;{status}
          </Clickable>
        );
    }

    return (
      <Stack direction="row" alignItems="center">
        <Step step={(statusCRA.findIndex((s) => s === status) + 1).toString()}></Step>&nbsp;{status}
      </Stack>
    );
  };

const ActivityDashboard = (props: ActivityDashboardProps) => {
  const [token, setToken] = useState<string | undefined>();
  const auth = useAuthStore((state) => state.auth);

  useEffect(() => {
    if (auth?.getAccessTokenSilently) {
      auth.getAccessTokenSilently().then((token) => {
        setToken(token);
      });
    }
  }, [auth]);

  const { t } = useTranslation(['actitivyDashboard', 'mission', 'invoiceStatus']);

  const invoiceStatusOptions = useMemo(() => getInvoiceStatusOptions(t), [t]);

  const [historyPopinState, setHistoryPopinState] = useState<{
    activityId: string | null;
    open: boolean;
    month: Date | null;
    missionId: string | null;
  }>({
    activityId: null,
    open: false,
    month: null,
    missionId: null,
  });
  const [fileViewerPath, setFileViewerPath] = useState<string | undefined>(undefined);
  const [nbDaysDetails, setNbDaysDetails] = useState<
    { nbDaysDetails: ActivityDayDetails[]; hoursPerDays: number; month: string } | undefined
  >(undefined);
  const [openActionSupplierInvoice, setOpenActionSupplierInvoice] = useState<{
    craDashboardItemGQL?: CRADashboardItemGQL;
    open: boolean;
  }>({ open: false });
  const [openActionCustomerInvoice, setOpenActionCustomerInvoice] = useState<{
    craDashboardItemGQL?: CRADashboardItemGQL;
    open: boolean;
  }>({ open: false });
  const [openActionForceCRA, setOpenActionForceCRA] = useState<{
    craDashboardItemGQL?: CRADashboardItemGQL;
    open: boolean;
  }>({ open: false });
  const [openDeclareZeroCRAPopin, setOpenDeclareZeroCRAPopin] = useState<{
    craDashboardItemGQL?: CRADashboardItemGQL;
    open: boolean;
  }>({ open: false });
  const closeHistoryPopin = () => {
    setHistoryPopinState({ activityId: null, open: false, month: null, missionId: null });
  };
  const openHistoryPopin = (activityId: Maybe<string>, month: Date, missionId: string) => {
    setHistoryPopinState({ activityId, open: true, month, missionId });
  };
  const [npsPopinState, setNpsPopinState] = useState<{
    data: {
      bloomer: string;
      contactOperationel: string;
      score: number;
      comment: string | null;
      date: Date;
    } | null;
    open: boolean;
  }>({
    data: null,
    open: false,
  });
  const closeNPSPopin = () => {
    setNpsPopinState({ data: null, open: false });
  };
  const openNPSPopin = (
    bloomer: string,
    contactOperationel: string,
    score: number,
    comment: string | null,
    date: Date,
  ) => {
    setNpsPopinState({ data: { bloomer, contactOperationel, score, comment, date }, open: true });
  };

  const verifiyZeroCRAActivityDaysValidity = (row: CRADashboardItemGQL) => {
    const allDaysNull =
      row.nbDaysWorked == null &&
      row.nbDaysMarkupWorked == null &&
      row.nbDaysMarkup2Worked == null &&
      row.nbDaysAstreinteWorked == null;
    if (allDaysNull && row.activityId) {
      throw new Error(
        "Impossible d'ouvrir l'action : le total des jours travaillés est indisponible pour cette ligne. Veuillez rafraîchir ou réessayer.",
      );
    }
    const total =
      (row.nbDaysWorked ?? 0) +
      (row.nbDaysMarkupWorked ?? 0) +
      (row.nbDaysMarkup2Worked ?? 0) +
      (row.nbDaysAstreinteWorked ?? 0);
    if (total < 0) {
      throw new Error("Impossible d'ouvrir l'action : données incohérentes (total des jours travaillés négatif).");
    }
    return true;
  };

  const renderStatusCellHanlder = (row: CRADashboardItemGQL) => () => {
    try {
      verifiyZeroCRAActivityDaysValidity(row);
    } catch (error) {
      notify('error', (error as Error).message);
      return;
    }
    if (canStatusSupportZeroCRA(row.status)) {
      if (
        isZeroCRAActivity({
          nbDaysWorked: row.nbDaysWorked ?? 0,
          nbDaysMarkupWorked: row.nbDaysMarkupWorked ?? 0,
          nbDaysMarkup2Worked: row.nbDaysMarkup2Worked ?? 0,
          astreinte: row.nbDaysAstreinteWorked ?? 0,
        })
      ) {
        setOpenDeclareZeroCRAPopin({ open: true, craDashboardItemGQL: row });
        return;
      }
    }
    setOpenActionForceCRA({ open: true, craDashboardItemGQL: row });
  };

  const { loading, rows, sendBloomerRelaunch, sendClientRelaunch } = props;

  const pageSize = 100;
  if (loading) return <LoaderSkeleton width={'100%'} />;

  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: 'CRA',
      headerClassName: 'header',
      children: [
        { field: 'missionId' },
        { field: 'activityId' },
        { field: 'month' },
        { field: 'status' },
        { field: 'craURL' },
      ],
    },
    {
      groupId: 'Bloomer',
      description: 'Bloomer',
      headerClassName: 'header',
      children: [
        { field: 'bloomerSocietyName' },
        { field: 'bloomerSocietyPortage' },
        { field: 'invoiceURL' },
        { field: 'bloomerScore' },
        { field: 'bloomerInvoiceBilled' },
        { field: 'paymentDate' },
        { field: 'bloomerInvoiceStatus' },
        { field: 'supplierInvoiceStatus' },
      ],
    },
    {
      groupId: 'Client',
      headerClassName: 'header',
      children: [
        { field: 'clientSocietyName' },
        { field: 'clientInvoiceStatus' },
        { field: 'contactOperationsName' },
        { field: 'clientScore' },
      ],
    },
  ];

  const columns: GridColDef<CRADashboardItemGQL>[] = [];
  if (props.missionMode) {
    columns.push({
      field: 'month',
      headerName: 'Mois',
      headerClassName: 'header',
      groupable: false,
      sortable: true,
      filterable: true,
      type: 'date',
      cellClassName: 'cellStyle',
      valueGetter: (value: GridValueGetter<CRADashboardItemGQL>, row: CRADashboardItemGQL) => {
        if (!row.activityMonth) {
          return;
        }
        return new Date(row.activityMonth);
      },
    });
  } else {
    columns.push({
      field: 'missionId',
      headerName: t('missionId', { ns: 'mission' }),
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      renderCell: (params: GridRenderCellParams<CRADashboardItemGQL>) => {
        if (params.rowNode.type === 'group') {
          return params.value;
        }
        const { missionId } = params.row;
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>{missionId}</span>
            <HrefLink href={`/missions/${missionId}`} target="_blank">
              <OpenInNewIcon fontSize="small" />
            </HrefLink>
          </Stack>
        );
      },
    });
  }
  columns.push(
    {
      field: 'activityId',
      headerName: t('activityId', { ns: 'mission' }),
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    },
    {
      field: 'status',
      headerName: t('status'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      type: 'singleSelect',
      valueOptions: statusCRA,
      minWidth: 218,
      valueFormatter: (value: string) => value,
      cellClassName: 'cellStyle',
      valueGetter: (value: GridValueGetter<CRADashboardItemGQL>, row: CRADashboardItemGQL) => row.status,
      renderCell: renderCRAStatusCell(renderStatusCellHanlder),
    },
    {
      field: 'craURL',
      headerName: t('craURL'),
      headerClassName: 'header',
      align: 'center',
      cellClassName: 'cellStyle',
      groupable: false,
      sortable: true,
      filterable: false,
      renderCell: (params: GridRenderCellParams<CRADashboardItemGQL>) => {
        return params.row.craURL ? (
          <ActionButton
            actionType="Preview"
            variant="contained"
            color="secondary"
            onClick={() => setFileViewerPath(params.row.craURL || '')}
            title={params.row.craURL}
          />
        ) : (
          <div>
            {params.row.nbDaysDetails && (
              <ActionButton
                actionType="Preview"
                variant="contained"
                color="secondary"
                onClick={() => {
                  if (params.row.nbDaysDetails && params.row.activityMonth && params.row.hoursPerDays) {
                    const eachDays = convertActivityToEachDaysArray(
                      new Date(params.row.activityMonth).toISOString(),
                      params.row.nbDaysDetails,
                    );
                    if (eachDays) {
                      setNbDaysDetails({
                        nbDaysDetails: eachDays,
                        hoursPerDays: params.row.hoursPerDays,
                        month: new Date(params.row.activityMonth).toISOString(),
                      });
                    }
                  }
                }}
                title={params.row.status}
              />
            )}
          </div>
        );
      },
    },
    {
      field: 'clientSocietyName',
      headerName: t('clientSocietyName', { ns: 'mission' }),
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    },
    {
      field: 'clientInvoiceStatus',
      headerName: t('clientInvoiceStatus'),
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      cellClassName: 'cellStyle',
      type: 'singleSelect',
      align: 'center',
      valueOptions: values(invoiceStatusOptions),
      renderCell: renderInvoiceStatusCell<CRADashboardItemGQL>(
        'clientInvoiceStatus',
        'clientInvoiceLastError',
        (row: CRADashboardItemGQL) => {
          return false;
        },
        t,
        (craDashboardItemGQL: CRADashboardItemGQL) => () => {
          setOpenActionCustomerInvoice({ open: true, craDashboardItemGQL });
          return undefined;
        },
      ),
    },
    {
      field: 'contactOperationsName',
      headerName: t('contactOperationsName'),
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    },
    {
      field: 'clientScore',
      headerName: t('clientScore'),
      headerClassName: 'header',
      sortable: true,
      groupable: false,
      filterable: true,
      type: 'number',
      cellClassName: (params: GridCellParams<CRADashboardItemGQL>) => {
        if (params.row.clientScore === null || params.row.clientScore > 6 || params.rowNode.type === 'group')
          return 'normalCell';
        return 'errorCell';
      },
      renderCell: (params: GridRenderCellParams<CRADashboardItemGQL>) => {
        const { clientComment, clientScoreDate, bloomerFullName, contactOperationsName, clientScore } = params.row;

        if (clientScore === null) return;
        return (
          <NPSScore
            onClick={() => {
              openNPSPopin(bloomerFullName, contactOperationsName, clientScore, clientComment, clientScoreDate as Date);
            }}
          >
            {clientScore}
          </NPSScore>
        );
      },
    },
    {
      field: 'bloomerFullName',
      headerName: t('bloomerFullName', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      minWidth: 200,
      cellClassName: 'cellStyle',
      valueGetter: (value: GridValueGetter<CRADashboardItemGQL>, row: CRADashboardItemGQL) => row.bloomerFullName,
    },
    {
      field: 'bloomerSocietyName',
      headerName: t('bloomerSocietyName', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      minWidth: 200,
      cellClassName: 'cellStyle',
      valueGetter: (value: GridValueGetter<CRADashboardItemGQL>, row: CRADashboardItemGQL) => row.bloomerSocietyName,
    },
    {
      field: 'bloomerSocietyPortage',
      headerName: t('bloomerSocietyPortage', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      type: 'boolean',
      cellClassName: 'cellStyle',
      valueGetter: (value: GridValueGetter<CRADashboardItemGQL>, row: CRADashboardItemGQL) =>
        row.bloomerSocietyPortage ?? false,
    },
    {
      field: 'bloomerScore',
      headerName: t('bloomerScore'),
      headerClassName: 'header',
      sortable: true,
      groupable: false,
      filterable: true,
      type: 'number',
      cellClassName: (params: GridCellParams<CRADashboardItemGQL>) => {
        if (params.row.bloomerScore === null || params.row.bloomerScore > 6 || params.rowNode.type === 'group')
          return 'normalCell';
        return 'errorCell';
      },
      renderCell: (params: GridRenderCellParams<CRADashboardItemGQL>) => {
        const { bloomerScore, bloomerFullName, bloomerComment, bloomerScoreDate, contactOperationsName } = params.row;

        if (bloomerScore === null) return;
        return (
          <NPSScore
            onClick={() => {
              openNPSPopin(
                bloomerFullName,
                contactOperationsName,
                bloomerScore,
                bloomerComment,
                bloomerScoreDate as Date,
              );
            }}
          >
            {bloomerScore}
          </NPSScore>
        );
      },
    },
    {
      field: 'invoiceURL',
      headerName: t('invoiceURL'),
      headerClassName: 'header',
      align: 'center',
      cellClassName: 'cellStyle',
      groupable: false,
      sortable: true,
      filterable: false,
      renderCell: (params: GridRenderCellParams<CRADashboardItemGQL>) => {
        return (
          params.row.invoiceURL && (
            <ActionButton
              actionType="Preview"
              variant="contained"
              color="secondary"
              onClick={() => setFileViewerPath(params.row.invoiceURL || '')}
              title={params.row.invoiceURL}
            />
          )
        );
      },
    },
    {
      field: 'bloomerInvoiceStatus',
      headerName: t('bloomerInvoiceBilled'),
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      align: 'center',
      type: 'singleSelect',
      cellClassName: 'cellStyle',
      valueOptions: values(invoiceStatusOptions),
      valueGetter: (value: GridValueGetter<CRADashboardItemGQL>, row: CRADashboardItemGQL) => row.bloomerInvoiceStatus,
      renderCell: renderInvoiceStatusCell<CRADashboardItemGQL>(
        'bloomerInvoiceStatus',
        'bloomerInvoiceLastError',
        (craDashboardItemGQL) => {
          return (
            !!craDashboardItemGQL.invoiceURL &&
            (craDashboardItemGQL.status === 'Validé client' || craDashboardItemGQL.status === 'Forcé Bloomays')
          );
        },
        t,
        (craDashboardItemGQL: CRADashboardItemGQL) => () => {
          setOpenActionSupplierInvoice({ open: true, craDashboardItemGQL });
          return undefined;
        },
      ),
    },
    {
      field: 'supplierInvoiceStatus',
      headerName: 'Statut fact. fourn.',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      align: 'center',
      type: 'singleSelect',
      valueOptions: [
        { value: 'created', label: '🤖 Créée API' },
        { value: 'imported', label: '📩 Importée' },
        { value: 'errored', label: '🚨 En erreur' },
        { value: 'refused', label: '⛔ Refusée' },
      ],
      cellClassName: 'cellStyle',
      valueGetter: (value: GridValueGetter<CRADashboardItemGQL>, row: CRADashboardItemGQL) =>
        row.supplierInvoiceStatus ?? null,
      renderCell: (params: GridRenderCellParams<CRADashboardItemGQL>) => {
        if (params.rowNode.type === 'group') {
          if (params.field !== '__row_group_by_columns_group__') return '';
          return (params.value as string) ?? '';
        }
        const status = params.row.supplierInvoiceStatus;
        const labels: Record<string, string> = {
          created: '🤖 Créée API',
          imported: '📩 Importée',
          errored: '🚨 En erreur',
          refused: '⛔ Refusée',
        };
        const icons: Record<string, string> = {
          created: '🤖',
          imported: '📩',
          errored: '🚨',
          refused: '⛔',
        };
        if (!status) return '🚧';
        const label = labels[status];
        const icon = icons[status];
        return (
          <IconButton size="small" title={label} variant="contained">
            {icon || label}
          </IconButton>
        );
      },
    },
    {
      field: 'paymentDate',
      headerName: t('paymentEstimed'),
      align: 'center',
      filterable: true,
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      type: 'date',
      valueGetter: (value: GridValueGetter<CRADashboardItemGQL>, row: CRADashboardItemGQL) => {
        if (row.invoiceURL && row.dateUploadInvoice) {
          return add({ days: row.bloomerPayDateDays || 45 })(new Date(row.dateUploadInvoice));
        } else if (row.invoiceURL === null && row.bloomerInvoiceBilled && row.activityMonth) {
          return pipe(lastDayOfMonth, add({ days: row.bloomerPayDateDays || 45 }))(new Date(row.activityMonth));
        }
      },
      renderCell: (params: GridRenderCellParams<CRADashboardItemGQL>) => {
        const item = params.row;
        if (item.invoiceURL && item.dateUploadInvoice) {
          return pipe(
            add({ days: item.bloomerPayDateDays || 45 }),
            format('dd/MM/yyyy'),
          )(new Date(item.dateUploadInvoice));
        } else if (item.invoiceURL === null && item.bloomerInvoiceBilled && item.activityMonth) {
          return pipe(
            lastDayOfMonth,
            add({ days: item.bloomerPayDateDays || 45 }),
            format('dd/MM/yyyy'),
          )(new Date(item.activityMonth));
        }
      },
    },
    {
      field: 'actions',
      headerClassName: 'header',
      sortable: false,
      groupable: false,
      filterable: false,
      cellClassName: 'actionCell',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'left',
      renderCell: (params: GridRenderCellParams<CRADashboardItemGQL>) => {
        if (params.rowNode.type === 'group') {
          return '';
        }
        const { missionId, activityId, status, signingDocumentId, clientInvoiceStatus, activityMonth } = params.row;
        return (
          <IconsContainer>
            <ActionButton
              actionType="Preview"
              variant="contained"
              onClick={() => {
                openHistoryPopin(activityId, activityMonth as Date, missionId);
              }}
              title={'Consulter historique'}
            />
            {activityMonth && (
              <CraRelaunchAction
                missionId={missionId}
                CRAStatus={status}
                signingDocumentId={signingDocumentId || undefined}
                sendBloomerRelaunch={sendBloomerRelaunch}
                sendClientRelaunch={sendClientRelaunch}
                month={activityMonth}
              />
            )}
          </IconsContainer>
        );
      },
    },
  );

  return (
    <div style={{ width: '100%' }}>
      <Modal
        open={!!fileViewerPath}
        onClose={() => setFileViewerPath(undefined)}
        additionnalCSS={{
          width: '822px',
          padding: '32px',
          height: '90%',
          border: 'none',
          borderRadius: '20px',
        }}
      >
        <FileViewer width="100%" height="100%" url={fileViewerPath as string} accessToken={token} />
      </Modal>
      <Modal open={openDeclareZeroCRAPopin.open} onClose={() => setOpenDeclareZeroCRAPopin({ open: false })}>
        {openDeclareZeroCRAPopin.craDashboardItemGQL && props.declareZeroActivityForMission ? (
          <ActionZeroCRA
            missionId={openDeclareZeroCRAPopin.craDashboardItemGQL.missionId}
            missionLabel={openDeclareZeroCRAPopin.craDashboardItemGQL.missionLabel}
            activityMonth={openDeclareZeroCRAPopin.craDashboardItemGQL.activityMonth ?? undefined}
            craStatus={openDeclareZeroCRAPopin.craDashboardItemGQL.status as CRAStatus}
            clickHandler={async () => {
              const row = openDeclareZeroCRAPopin.craDashboardItemGQL as CRADashboardItemGQL;
              const month = row.activityMonth ? new Date(row.activityMonth) : props.currentMonth;
              await props.declareZeroActivityForMission?.({
                variables: {
                  missionId: row.missionId,
                  month,
                },
              });
              setOpenDeclareZeroCRAPopin({ open: false });
            }}
          />
        ) : undefined}
      </Modal>
      <Modal
        open={!!nbDaysDetails}
        onClose={() => setNbDaysDetails(undefined)}
        additionnalCSS={{
          width: '822px',
          padding: '32px',
          height: '90%',
          border: 'none',
          borderRadius: '20px',
          overflow: 'scroll',
        }}
      >
        {nbDaysDetails && (
          <>
            <SummaryDaysWorked
              month={nbDaysDetails.month}
              daysWorked={calculPerDay(nbDaysDetails.nbDaysDetails, ActivityDayType.nbDaysWorked)}
              daysMarkup={calculPerDay(nbDaysDetails.nbDaysDetails, ActivityDayType.nbDaysMarkupWorked)}
              daysMarkup2={calculPerDay(nbDaysDetails.nbDaysDetails, ActivityDayType.nbDaysMarkup2Worked)}
              daysAstreinte={calculPerDay(nbDaysDetails.nbDaysDetails, ActivityDayType.astreinte)}
            />
            <FormCRA
              eachDays={nbDaysDetails.nbDaysDetails || []}
              readOnly={true}
              onCRAUpdate={() => void 0}
              hoursPerDay={nbDaysDetails.hoursPerDays}
            />
          </>
        )}
      </Modal>
      <Modal
        open={historyPopinState.open}
        onClose={closeHistoryPopin}
        additionnalCSS={{
          width: '980px',
          padding: '32px',
          border: 'none',
          borderRadius: '20px',
        }}
      >
        <ChronologicalHistory
          activityId={historyPopinState.activityId}
          missionId={historyPopinState.missionId!}
          month={historyPopinState.month!}
        />
      </Modal>
      <Modal
        open={npsPopinState.open}
        onClose={closeNPSPopin}
        additionnalCSS={{
          width: '622px',
          padding: '32px',
          border: 'none',
          borderRadius: '20px',
        }}
      >
        {npsPopinState.data ? (
          <DetailsSectionCard
            title={`Note du NPS du Client ${npsPopinState.data.contactOperationel} sur le Bloomer ${
              npsPopinState.data.bloomer
            } du ${format('dd/MM/yyy', new Date(npsPopinState.data.date))}`}
            details={`Score: ${npsPopinState.data.score}`}
            text={`Commentaire: ${npsPopinState.data.comment || 'Pas de commentaire.'}`}
          />
        ) : (
          ''
        )}
      </Modal>
      <Modal open={openActionSupplierInvoice.open} onClose={() => setOpenActionSupplierInvoice({ open: false })}>
        {openActionSupplierInvoice.craDashboardItemGQL ? (
          <ActionInvoice
            mode="supplier"
            invoiceLabel={openActionSupplierInvoice.craDashboardItemGQL.activityId as string}
            activityId={openActionSupplierInvoice.craDashboardItemGQL.activityId as string}
            lastError={openActionSupplierInvoice.craDashboardItemGQL.bloomerInvoiceLastError as string}
            invoiceStatus={openActionSupplierInvoice.craDashboardItemGQL.bloomerInvoiceStatus as InvoiceStatus}
            supplierInvoiceStatus={openActionSupplierInvoice.craDashboardItemGQL.supplierInvoiceStatus}
            externalSupplierInvoiceId={openActionSupplierInvoice.craDashboardItemGQL.externalSupplierInvoiceId}
            clickHandler={async (force: boolean, byPass: boolean) => {
              await props.createActivitySupplierInvoice({
                variables: {
                  activityRecordID: openActionSupplierInvoice.craDashboardItemGQL?.activityId as string,
                  force: force,
                  byPassComplianceCheck: byPass,
                },
              });
              setOpenActionSupplierInvoice({ open: false });
            }}
          />
        ) : undefined}
      </Modal>
      <Modal open={openActionCustomerInvoice.open} onClose={() => setOpenActionCustomerInvoice({ open: false })}>
        {openActionCustomerInvoice.craDashboardItemGQL ? (
          <ActionInvoice
            mode="customer"
            invoiceLabel={openActionCustomerInvoice.craDashboardItemGQL.activityId as string}
            activityId={openActionCustomerInvoice.craDashboardItemGQL.activityId as string}
            lastError={openActionCustomerInvoice.craDashboardItemGQL.clientInvoiceLastError as string}
            invoiceStatus={openActionCustomerInvoice.craDashboardItemGQL?.clientInvoiceStatus as InvoiceStatus}
            clickHandler={async (force: boolean) => {
              await props.createActivityCustomerInvoice({
                variables: {
                  activityRecordID: openActionCustomerInvoice.craDashboardItemGQL?.activityId as string,
                  force: force,
                },
              });
              setOpenActionCustomerInvoice({ open: false });
            }}
          />
        ) : undefined}
      </Modal>
      <Modal open={openActionForceCRA.open} onClose={() => setOpenActionForceCRA({ open: false })}>
        {openActionForceCRA.craDashboardItemGQL ? (
          <ActionForceCRA
            missionLabel={openActionForceCRA.craDashboardItemGQL.missionLabel}
            activityMonth={openActionForceCRA.craDashboardItemGQL.activityMonth ?? undefined}
            activityId={openActionForceCRA.craDashboardItemGQL.activityId as string}
            craStatus={openActionForceCRA.craDashboardItemGQL?.status as CRAStatus}
            clickHandler={async (force: boolean) => {
              await props.forceCRA({
                variables: {
                  activityRecordID: openActionForceCRA.craDashboardItemGQL?.activityId as string,
                  force: force,
                },
              });
              setOpenActionForceCRA({ open: false });
            }}
          />
        ) : undefined}
      </Modal>
      <DataGrid
        uniqueDatagridId={props.uniqueActivityDashboardId}
        initialState={{
          sorting: {
            sortModel: [
              { field: 'bloomerFullName', sort: 'asc' },
              { field: 'month', sort: 'desc' },
            ],
          },
          pinnedColumns: {
            left: ['actions', 'bloomerFullName'],
          },
        }}
        fetchLoading={loading}
        getRowClassName={(params: GridRowClassNameParams) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        hideFooterPagination={false}
        sortingMode="client"
        filterMode="client"
        rows={rows}
        getRowId={(row: CRADashboardItemGQL) => `${row.missionId}-${row.activityId}`}
        columns={columns}
        pageSize={pageSize}
        pagination={true}
        columnGroupingModel={columnGroupingModel}
        sx={{
          width: '100%',
        }}
      />
    </div>
  );
};

export default ActivityDashboard;

const DataGrid = styled(RawDataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.neutral[10],
  },
}));

const NPSScore = styled('div')(() => ({
  margin: 'auto',
  border: 'none',
  '&:hover': {
    cursor: 'pointer',
  },
}));

const IconsContainer = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
}));

const Clickable = styled(Stack)(() => ({
  '&:hover': {
    cursor: 'pointer',
  },
}));
